/**
 * bodyMotionRootMode — which root mode a generated kimodo clip is baked in, decided
 * ONCE for both the wizard's live preview and the GLB it saves.
 *
 * It exists because the two used to decide independently and disagreed. The preview
 * (bodyMotionNav.playTracks) branched on `buildRootDrive(tracks)`, which is non-null for
 * any root track with >= 2 frames — every kimodo payload — so it was ALWAYS 'extract'
 * and never read the author's flag at all; the export (bodyMotionExport) hard-coded
 * 'travel' and then dropped `pelvis.position` when the flag said in-place. So ticking
 * "In place" left the preview jumping and walking while the stored clip did neither,
 * with nothing on screen saying so. Reported from the other end: a generated jump saved
 * in place, then played through a prefab, "does the action locked in place".
 *
 * THE INVARIANT: preview and save agree on WHETHER the motion carries root motion.
 *
 * They may differ on WHERE it is carried, and that difference is by construction, not a
 * bug: a live preview has an avatar group to drive (which is also what lets the clip
 * blend out without sliding the character back), a stored clip has no group at all, so
 * its travel has to ride `pelvis.position`. Whether the motion EXISTS is not allowed to
 * differ — that is the part a viewer reads off the screen and expects to have saved.
 */

/**
 * @param {object} [opts]
 * @param {boolean} [opts.keepRootMotion=true] the author's flag, in the sense the file
 *   stores it (the wizard's "In place" checkbox is its INVERSE). Defaults TRUE so a
 *   caller that never asks keeps its travel — walk-here and the `__GEN_BODY_MOTION_ON_*`
 *   debug seams never set it and must not lose their root motion.
 * @param {boolean} [opts.live=false] true for the live preview (an avatar group exists
 *   to carry the travel), false when baking a clip that has to carry its own.
 * @param {boolean} [opts.hasRootDrive=false] whether the payload yielded a root drive
 *   (`buildRootDrive`) — only meaningful when `live`.
 * @returns {'lock'|'extract'|'travel'} the `rootMode` for buildBodyMotionClip
 */
export function resolveGeneratedRootMode({ keepRootMotion = true, live = false, hasRootDrive = false } = {}) {
  // In place: no pelvis position track at all, in either consumer. Asked FIRST, so the
  // author's flag cannot be overridden by the presence of a drive to extract.
  if (keepRootMotion === false) return 'lock'
  // Live, with a drive: the group carries the trajectory and the pelvis keeps the
  // residual sway plus the whole vertical.
  if (live && hasRootDrive) return 'extract'
  // A clip that must carry its own travel — every export, and a live payload with no
  // root track to extract.
  return 'travel'
}

/**
 * Does a clip baked in this mode carry root motion?
 *
 * The one thing the preview and the export must answer identically for a given flag —
 * asserted as such in tests/unit/bodyMotionRootMode.test.mjs, so the two cannot drift
 * apart again without a failure.
 * @param {'lock'|'extract'|'travel'} mode
 * @returns {boolean}
 */
export function carriesRootMotion(mode) {
  return mode !== 'lock'
}
